import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Shield, Plus, Users, Loader2, Send, LogIn, Crown, Star, DoorOpen, Search, UserMinus, Link2, Hash, MessageSquare, Trophy, Zap, Swords, Check, X, Clock,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Spinner, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const EMOJIS = ["🛡️", "⚔️", "🐉", "🦊", "🚀", "🌟", "🔥", "🎮", "👾", "🏆"];
const ROLE_ICON = { owner: Crown, officer: Star };

export default function Guilds() {
  const [active, setActive] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const g = searchParams.get("g");
    if (g) setActive(g);
  }, [searchParams]);
  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="guilds-page">
      <PageHeader title="Guilds & Clans" icon={Shield}
        subtitle="Team up with other creators and players — build a crew, chat, and grow together."
        action={<Button onClick={() => setCreateOpen(true)} className="rounded-full" data-testid="create-guild-btn"><Plus className="w-4 h-4" /> Create guild</Button>} />
      <GuildProRibbon onOpenGuild={setActive} onCreate={() => setCreateOpen(true)} />
      <Tabs defaultValue="mine">
        <TabsList className="mb-5">
          <TabsTrigger value="mine" data-testid="guilds-tab-mine"><Shield className="w-4 h-4 mr-1.5" /> My Guilds</TabsTrigger>
          <TabsTrigger value="browse" data-testid="guilds-tab-browse"><Search className="w-4 h-4 mr-1.5" /> Browse</TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="guilds-tab-leaderboard"><Trophy className="w-4 h-4 mr-1.5" /> Leaderboard</TabsTrigger>
        </TabsList>
        <TabsContent value="mine"><MyGuilds onOpen={setActive} onCreate={() => setCreateOpen(true)} /></TabsContent>
        <TabsContent value="browse"><BrowseGuilds onOpen={setActive} /></TabsContent>
        <TabsContent value="leaderboard"><GuildLeaderboard onOpen={setActive} /></TabsContent>
      </Tabs>
      <CreateGuildDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(g) => { setCreateOpen(false); setActive(g.id); }} />
      {active && <GuildDialog guildId={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function GuildProRibbon({ onOpenGuild, onCreate }) {
  const [info, setInfo] = useState(null);
  const [mine, setMine] = useState(null);
  const [hidden, setHidden] = useState(() => localStorage.getItem("gv_hide_guildpro_ribbon") === "1");

  useEffect(() => {
    if (hidden) return;
    api.get("/guilds/pro/info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/guilds/mine").then((r) => setMine(r.data.guilds || [])).catch(() => setMine([]));
  }, [hidden]);

  if (hidden || !info || mine === null) return null;
  const ownedGuilds = mine.filter((g) => g.my_role === "owner");
  // If every owned guild is already Pro, no need to upsell.
  if (ownedGuilds.length > 0 && ownedGuilds.every((g) => g.is_pro)) return null;
  const target = ownedGuilds.find((g) => !g.is_pro);

  const dismiss = () => { localStorage.setItem("gv_hide_guildpro_ribbon", "1"); setHidden(true); };
  const cta = () => (target ? onOpenGuild(target.id) : onCreate());

  return (
    <div className="relative overflow-hidden rounded-2xl mb-5 p-4 sm:p-5 bg-gradient-to-r from-amber-500/15 via-yellow-500/10 to-transparent border border-amber-400/30" data-testid="guild-pro-ribbon">
      <div className="absolute -right-6 -top-6 text-[7rem] opacity-10 select-none pointer-events-none">👑</div>
      <button onClick={dismiss} className="absolute top-2.5 right-2.5 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]" data-testid="guild-pro-ribbon-dismiss" aria-label="Dismiss"><X className="w-4 h-4" /></button>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 relative">
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-lg flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" /> Go Guild Pro</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-0.5">
            Bigger rosters (up to {info.max_members}), custom banners & emoji, and <b className="text-amber-500">featured Guild Wars</b> placement.
            <span className="hidden sm:inline"> From {info.price_credits} credits / {info.duration_days} days.</span>
          </p>
        </div>
        <Button onClick={cta} className="rounded-full shrink-0 bg-gradient-to-r from-amber-400 to-yellow-500 text-black hover:opacity-90 font-bold" data-testid="guild-pro-ribbon-cta">
          {target ? "Upgrade my guild" : "Create a guild"} · {info.price_credits} cr
        </Button>
      </div>
    </div>
  );
}

function MyGuilds({ onOpen, onCreate }) {
  const [guilds, setGuilds] = useState(null);
  useEffect(() => { api.get("/guilds/mine").then((r) => setGuilds(r.data.guilds)).catch(() => setGuilds([])); }, []);
  if (!guilds) return <Spinner />;
  if (guilds.length === 0) return <EmptyState emoji="🛡️" title="You're not in a guild yet" text="Create your own crew or browse public guilds to join."
    action={<Button onClick={onCreate} className="rounded-full"><Plus className="w-4 h-4" /> Create guild</Button>} />;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-guilds-grid">
      {guilds.map((g) => <GuildCard key={g.id} g={g} onOpen={onOpen} showRole />)}
    </div>
  );
}

function BrowseGuilds({ onOpen }) {
  const [guilds, setGuilds] = useState(null);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("popular");
  const load = useCallback(() => {
    api.get("/guilds", { params: { ...(q ? { q } : {}), sort } })
      .then((r) => { setGuilds(r.data.guilds); setStats(r.data.stats || null); })
      .catch(() => setGuilds([]));
  }, [q, sort]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const SORTS = [["popular", "🔥 Popular"], ["active", "⚡ Active"], ["new", "🆕 New"]];
  return (
    <div>
      {stats && (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="guild-stats">
          <StatChip label="Guilds" value={stats.total_guilds} />
          <StatChip label="Members" value={stats.total_members} />
          <StatChip label="Public" value={stats.public_guilds} />
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search guilds…" className="pl-9" data-testid="guild-search" />
        </div>
        <div className="flex gap-1.5" data-testid="guild-sort">
          {SORTS.map(([v, l]) => (
            <button key={v} onClick={() => setSort(v)} data-testid={`guild-sort-${v}`}
              className={`px-3 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${sort === v ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--accent))]"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {!guilds ? <Spinner /> : guilds.length === 0 ? (
        <EmptyState emoji="🔍" title="No public guilds found" text="Be the first to create one for the community." />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="browse-guilds-grid">
          {guilds.map((g, i) => <GuildCard key={g.id} g={g} onOpen={onOpen} rank={sort === "popular" ? i + 1 : null} />)}
        </div>
      )}
    </div>
  );
}

const StatChip = ({ label, value }) => (
  <div className="rounded-full bg-[hsl(var(--secondary))] px-3.5 py-1.5 text-xs font-semibold flex items-center gap-1.5">
    <span className="text-[hsl(var(--foreground))]">{(value ?? 0).toLocaleString()}</span>
    <span className="text-[hsl(var(--muted-foreground))]">{label}</span>
  </div>
);

const LevelBadge = ({ level, className = "" }) => (
  <span className={`inline-flex items-center gap-1 rounded-full bg-amber-400/15 text-amber-500 px-2 py-0.5 text-[10px] font-bold ${className}`} data-testid="guild-level-badge">
    <Zap className="w-3 h-3" /> Lv {level}
  </span>
);

const ProBadge = ({ className = "" }) => (
  <span className={`inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-black px-2 py-0.5 text-[10px] font-extrabold shadow-sm ${className}`} data-testid="guild-pro-badge">
    <Crown className="w-3 h-3" /> PRO
  </span>
);

function GuildProPanel({ g, guildId, isOwner, canManage, onChanged }) {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null); // 'upgrade' | 'customize'
  const [banner, setBanner] = useState(g.banner);
  const [emoji, setEmoji] = useState(g.emoji);

  const openUpgrade = async () => {
    try { const { data } = await api.get("/guilds/pro/info"); setInfo(data); setMode("upgrade"); }
    catch { toast.error("Couldn't load Guild Pro info"); }
  };
  const openCustomize = async () => {
    try { const { data } = await api.get("/guilds/pro/info"); setInfo(data); setBanner(g.banner); setEmoji(g.emoji); setMode("customize"); }
    catch { toast.error("Couldn't load options"); }
  };
  const upgrade = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/guilds/${guildId}/upgrade-pro`); onChanged(data); setMode(null); toast.success("👑 Guild upgraded to PRO!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveCustom = async () => {
    setBusy(true);
    try { const { data } = await api.patch(`/guilds/${guildId}/customize`, { banner, emoji }); onChanged(data); setMode(null); toast.success("Guild look updated!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (g.is_pro) {
    const toggleRenew = async (v) => {
      try { const { data } = await api.patch(`/guilds/${guildId}/auto-renew`, { enabled: v }); onChanged(data); toast.success(v ? "Auto-renew on — we'll keep your guild Pro." : "Auto-renew off."); }
      catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };
    const expiry = g.pro_until ? new Date(g.pro_until).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null;
    return (
      <div className="rounded-xl bg-gradient-to-r from-amber-400/10 to-yellow-500/10 border border-amber-400/30 px-3.5 py-2.5 flex items-center gap-2 flex-wrap" data-testid="guild-pro-active">
        <ProBadge />
        <span className="text-xs text-[hsl(var(--muted-foreground))]">Pro active{expiry ? ` · renews/expires ${expiry}` : ""}</span>
        {isOwner && (
          <label className="flex items-center gap-1.5 text-xs ml-1 cursor-pointer" data-testid="guild-autorenew-toggle">
            <Switch checked={!!g.pro_auto_renew} onCheckedChange={toggleRenew} /> Auto-renew
          </label>
        )}
        {canManage && <Button size="sm" variant="outline" className="rounded-full ml-auto h-7" onClick={openCustomize} data-testid="guild-customize-btn"><Crown className="w-3.5 h-3.5" /> Customize</Button>}
        {mode === "customize" && info && (
          <div className="w-full mt-2 space-y-2" data-testid="guild-customize-panel">
            <div>
              <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Banner</span>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {info.banners.map((b) => <button key={b} onClick={() => setBanner(b)} className={`w-6 h-6 rounded-full ${banner === b ? "ring-2 ring-offset-2 ring-offset-[hsl(var(--card))] ring-white" : ""}`} style={{ background: b }} data-testid={`pro-banner-${b}`} />)}
              </div>
            </div>
            <div>
              <span className="text-[11px] text-[hsl(var(--muted-foreground))]">Emoji</span>
              <div className="flex flex-wrap gap-1 mt-1">
                {info.emojis.map((e) => <button key={e} onClick={() => setEmoji(e)} className={`w-7 h-7 rounded-lg text-lg grid place-items-center ${emoji === e ? "bg-[hsl(var(--primary))]/20 ring-1 ring-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--accent))]"}`}>{e}</button>)}
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" className="rounded-full" disabled={busy} onClick={saveCustom} data-testid="guild-customize-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save look"}</Button>
              <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setMode(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </div>
    );
  }
  if (!isOwner) return null;
  return (
    <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 px-3.5 py-2.5" data-testid="guild-pro-upsell">
      {mode !== "upgrade" ? (
        <div className="flex items-center gap-2 flex-wrap">
          <Crown className="w-4 h-4 text-amber-500" />
          <span className="text-xs font-semibold">Level up your guild with <span className="text-amber-500">Guild Pro</span></span>
          <Button size="sm" className="rounded-full ml-auto h-7 bg-gradient-to-r from-amber-400 to-yellow-500 text-black hover:opacity-90" onClick={openUpgrade} data-testid="guild-upgrade-btn">Upgrade</Button>
        </div>
      ) : info && (
        <div data-testid="guild-upgrade-panel">
          <div className="flex items-center gap-2 mb-2"><Crown className="w-4 h-4 text-amber-500" /><span className="font-bold text-sm">Guild Pro</span></div>
          <ul className="text-xs text-[hsl(var(--muted-foreground))] space-y-0.5 mb-2">
            <li>• Member cap {info.free_max_members} → <b className="text-[hsl(var(--foreground))]">{info.max_members}</b></li>
            <li>• Custom banner colours & premium emoji</li>
            <li>• Featured (priority) placement in Guild Wars</li>
            <li>• A shiny PRO badge everywhere</li>
          </ul>
          <div className="flex items-center gap-2">
            <Button size="sm" className="rounded-full bg-gradient-to-r from-amber-400 to-yellow-500 text-black hover:opacity-90" disabled={busy || !info.can_afford} onClick={upgrade} data-testid="guild-upgrade-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `${info.price_credits} credits / ${info.duration_days}d`}
            </Button>
            <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setMode(null)}>Cancel</Button>
            {!info.can_afford && <span className="text-[11px] text-red-400">Not enough credits ({info.credits_remaining})</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function GuildLeaderboard({ onOpen }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/guilds/leaderboard").then((r) => setData(r.data)).catch(() => setData({ leaderboard: [] })); }, []);
  if (!data) return <Spinner />;
  const rows = data.leaderboard || [];
  if (rows.length === 0) return <EmptyState emoji="🏆" title="No guilds ranked yet" text="Create a guild and earn XP by posting and recruiting to climb the ladder." />;
  return (
    <div>
      {data.my_rank && (
        <div className="rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 px-4 py-2.5 mb-4 text-sm font-semibold flex items-center gap-2" data-testid="my-guild-rank">
          <Trophy className="w-4 h-4 text-[hsl(var(--primary))]" /> Your guild is ranked #{data.my_rank} in the community
        </div>
      )}
      <div className="space-y-2" data-testid="guild-leaderboard">
        {rows.map((g) => {
          const medal = g.rank <= 3 ? ["🥇", "🥈", "🥉"][g.rank - 1] : null;
          const p = g.progress || {};
          const pct = p.xp_for_next ? Math.min(100, (p.xp_into_level / p.xp_for_next) * 100) : 0;
          return (
            <button key={g.id} onClick={() => onOpen(g.id)} data-testid={`leaderboard-row-${g.id}`}
              className={`w-full text-left rounded-xl border p-3 flex items-center gap-3 card-hover ${g.is_member ? "border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))] bg-[hsl(var(--card))]"}`}>
              <div className="w-8 text-center font-bold text-lg shrink-0">{medal || <span className="text-[hsl(var(--muted-foreground))] text-sm">#{g.rank}</span>}</div>
              <span className="w-10 h-10 rounded-lg grid place-items-center text-2xl shrink-0" style={{ background: `${g.banner}22` }}>{g.emoji}</span>
              <div className="min-w-0 flex-1">
                <div className="font-bold truncate flex items-center gap-1.5">{g.name} <span className="text-[10px] font-mono text-[hsl(var(--muted-foreground))]">[{g.tag}]</span> {g.is_pro && <ProBadge />} {g.is_member && <Badge variant="secondary" className="text-[9px]">Yours</Badge>}</div>
                <div className="mt-1 h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div className="text-right shrink-0">
                <LevelBadge level={g.level} />
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">{g.xp.toLocaleString()} XP · {g.member_count} 👥</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function GuildCard({ g, onOpen, showRole, rank }) {
  const RoleIcon = ROLE_ICON[g.my_role];
  const medal = rank && rank <= 3 ? ["🥇", "🥈", "🥉"][rank - 1] : null;
  return (
    <button onClick={() => onOpen(g.id)} data-testid={`guild-${g.id}`}
      className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden card-hover relative">
      {medal && <span className="absolute top-2 right-2 text-xl drop-shadow z-10" data-testid={`guild-rank-${g.id}`}>{medal}</span>}
      <div className="h-16 flex items-center px-4 gap-3" style={{ background: `linear-gradient(135deg, ${g.banner}, ${g.banner}99)` }}>
        <span className="text-3xl drop-shadow">{g.emoji}</span>
        <div className="min-w-0">
          <div className="font-bold text-white truncate flex items-center gap-1.5">{g.name} {g.is_pro && <ProBadge />} {RoleIcon && <RoleIcon className="w-3.5 h-3.5" />}</div>
          <div className="text-[11px] text-white/80 font-mono flex items-center gap-1.5">[{g.tag}]{typeof g.level === "number" && <span className="bg-black/25 rounded px-1.5 py-0.5 font-sans font-bold">Lv {g.level}</span>}</div>
        </div>
      </div>
      <div className="p-3.5">
        {g.description && <p className="text-xs text-[hsl(var(--muted-foreground))] line-clamp-2 mb-2">{g.description}</p>}
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-3 text-[hsl(var(--muted-foreground))]">
            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {g.member_count}</span>
            {typeof g.posts_count === "number" && <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {g.posts_count}</span>}
          </div>
          {g.is_member ? <Badge variant="secondary" className="text-[10px]">Joined</Badge> : <Badge variant="outline" className="text-[10px]">Open</Badge>}
        </div>
      </div>
    </button>
  );
}

function CreateGuildDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [emoji, setEmoji] = useState("🛡️");
  const [isPublic, setIsPublic] = useState(true);
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState("");

  useEffect(() => { if (open) { setName(""); setTag(""); setDescription(""); setEmoji("🛡️"); setIsPublic(true); setCode(""); } }, [open]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/guilds", { name, tag, description, emoji, is_public: isPublic });
      toast.success("Guild created! 🛡️");
      onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const join = async () => {
    if (!code.trim()) return;
    setBusy(true);
    try { const { data } = await api.post("/guilds/join", { code: code.trim().toUpperCase() }); toast.success("Joined!"); onCreated(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="create-guild-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5" /> Create a guild</DialogTitle>
          <DialogDescription>Start a crew for your friends and fans.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-[1fr_auto] gap-3">
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} placeholder="e.g. Pixel Pioneers" className="mt-1" data-testid="guild-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Tag</label>
              <Input value={tag} onChange={(e) => setTag(e.target.value.toUpperCase().slice(0, 5))} placeholder="PIX" className="mt-1 w-24 uppercase font-mono" data-testid="guild-tag" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Emoji</label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {EMOJIS.map((e) => (
                <button key={e} onClick={() => setEmoji(e)} className={`text-xl w-9 h-9 rounded-lg grid place-items-center ${emoji === e ? "bg-[hsl(var(--primary))]/20 ring-1 ring-[hsl(var(--primary))]" : "bg-[hsl(var(--secondary))]"}`}>{e}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-[hsl(var(--muted-foreground))]">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={300} placeholder="What's your guild about?" className="mt-1" data-testid="guild-description" />
          </div>
          <div className="flex gap-2">
            <button onClick={() => setIsPublic(true)} className={`flex-1 rounded-xl border p-2.5 text-sm font-semibold ${isPublic ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`} data-testid="guild-public">🌍 Public</button>
            <button onClick={() => setIsPublic(false)} className={`flex-1 rounded-xl border p-2.5 text-sm font-semibold ${!isPublic ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`} data-testid="guild-private">🔒 Invite only</button>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <div className="flex gap-2 mr-auto w-full sm:w-auto">
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Join by code" maxLength={8} className="uppercase tracking-widest" data-testid="guild-join-code" />
            <Button variant="outline" className="rounded-full shrink-0" onClick={join} disabled={busy} data-testid="guild-join-code-btn"><LogIn className="w-4 h-4" /></Button>
          </div>
          <Button className="rounded-full" onClick={create} disabled={busy || name.trim().length < 2 || tag.trim().length < 2} data-testid="confirm-create-guild">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GuildDialog({ guildId, onClose }) {
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [posts, setPosts] = useState([]);
  const [msg, setMsg] = useState("");
  const endRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/guilds/${guildId}`); setG(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onClose(); }
  }, [guildId, onClose]);
  const loadPosts = useCallback(async () => {
    try { const { data } = await api.get(`/guilds/${guildId}/posts`); setPosts(data.posts); } catch { /* not a member */ }
  }, [guildId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!g?.is_member) return;
    loadPosts();
    const t = setInterval(loadPosts, 5000);
    return () => clearInterval(t);
  }, [g?.is_member, loadPosts]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [posts.length]);

  const join = async () => {
    try { const { data } = await api.post(`/guilds/${guildId}/join`); setG(data); toast.success("Joined the guild!"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const leave = async () => {
    try { await api.post(`/guilds/${guildId}/leave`); toast.success("Left the guild."); onClose(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const send = async () => {
    if (!msg.trim()) return;
    const body = msg.trim(); setMsg("");
    try { const { data } = await api.post(`/guilds/${guildId}/posts`, { body }); setPosts((p) => [...p, data]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const kick = async (uid) => {
    try { const { data } = await api.delete(`/guilds/${guildId}/members/${uid}`); setG(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setRole = async (uid, role) => {
    try { const { data } = await api.post(`/guilds/${guildId}/members/${uid}/role`, { role }); setG(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const copyCode = () => { navigator.clipboard?.writeText(g.code); toast.success("Invite code copied!"); };

  if (!g) return null;
  const isOwner = g.my_role === "owner";
  const canManage = g.my_role === "owner" || g.my_role === "officer";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="guild-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <span className="text-2xl">{g.emoji}</span> {g.name}
            <Badge variant="secondary" className="font-mono text-[10px]">[{g.tag}]</Badge>
            {g.is_pro && <ProBadge />}
            {g.is_member && g.code && (
              <button onClick={copyCode} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-[hsl(var(--border))] px-3 py-1 text-xs font-mono hover:bg-[hsl(var(--accent))]" data-testid="guild-code-copy">
                <Hash className="w-3 h-3" />{g.code} <Link2 className="w-3 h-3" />
              </button>
            )}
          </DialogTitle>
          <DialogDescription>{g.description || "No description."} · {g.member_count} members</DialogDescription>
        </DialogHeader>

        {g.is_member && <GuildProPanel g={g} guildId={guildId} isOwner={isOwner} canManage={canManage} onChanged={setG} />}

        {g.progress && (
          <div className="rounded-xl bg-[hsl(var(--secondary))]/50 px-3.5 py-2.5 -mt-1" data-testid="guild-xp-bar">
            <div className="flex items-center justify-between text-xs mb-1.5">
              <span className="font-bold flex items-center gap-1 text-amber-500"><Zap className="w-3.5 h-3.5" /> Level {g.level}</span>
              <span className="text-[hsl(var(--muted-foreground))]">{g.progress.xp_into_level} / {g.progress.xp_for_next} XP to Lv {g.level + 1}</span>
            </div>
            <div className="h-2 rounded-full bg-[hsl(var(--background))] overflow-hidden">
              <div className="h-full bg-gradient-to-r from-amber-400 to-amber-500 transition-all" style={{ width: `${g.progress.xp_for_next ? Math.min(100, (g.progress.xp_into_level / g.progress.xp_for_next) * 100) : 0}%` }} />
            </div>
            <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1.5">Earn XP: +5 per guild post · +25 per new member · +10 per multiplayer win. Play, recruit and win to climb the leaderboard!</div>
          </div>
        )}

        {!g.is_member ? (
          <div className="py-8 text-center">
            <Button onClick={join} className="rounded-full" data-testid="guild-join-btn"><LogIn className="w-4 h-4" /> Join this guild</Button>
          </div>
        ) : (
          <>
          <GuildBattle guildId={guildId} canManage={canManage} onChanged={load} />
          <div className="grid md:grid-cols-2 gap-4 flex-1 min-h-0">
            {/* Members */}
            <div className="flex flex-col min-h-0">
              <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Members ({g.member_count})</div>
              <div className="space-y-1.5 overflow-y-auto pr-1" data-testid="guild-members">
                {(g.members || []).map((m) => {
                  const RIcon = ROLE_ICON[m.role];
                  return (
                    <div key={m.user_id} className="flex items-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-3 py-2 text-sm">
                      <span className="text-lg">{m.avatar}</span>
                      <span className="truncate flex-1">{m.name}{m.user_id === user?.id && " (you)"}</span>
                      {RIcon && <RIcon className="w-4 h-4 text-amber-400" />}
                      {isOwner && m.user_id !== user?.id && (
                        <div className="flex gap-1">
                          <button onClick={() => setRole(m.user_id, m.role === "officer" ? "member" : "officer")} title="Toggle officer" className="text-[hsl(var(--muted-foreground))] hover:text-amber-400"><Star className="w-3.5 h-3.5" /></button>
                          <button onClick={() => kick(m.user_id)} title="Remove" className="text-[hsl(var(--muted-foreground))] hover:text-red-500" data-testid={`kick-${m.user_id}`}><UserMinus className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                      {canManage && !isOwner && m.role === "member" && m.user_id !== user?.id && (
                        <button onClick={() => kick(m.user_id)} title="Remove" className="text-[hsl(var(--muted-foreground))] hover:text-red-500" data-testid={`kick-${m.user_id}`}><UserMinus className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {/* Feed/chat */}
            <div className="flex flex-col min-h-0 border-l border-[hsl(var(--border))] md:pl-4">
              <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] mb-2">Guild feed</div>
              <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[160px]" data-testid="guild-feed">
                {posts.length === 0 ? (
                  <div className="text-xs text-[hsl(var(--muted-foreground))] py-4 text-center">No posts yet — say hello to your guild 👋</div>
                ) : posts.map((p) => (
                  <div key={p.id} className="text-sm"><span className="font-semibold">{p.avatar} {p.name}:</span> <span className="text-[hsl(var(--muted-foreground))]">{p.body}</span></div>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 mt-2">
                <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Post to your guild…" maxLength={500} data-testid="guild-post-input" />
                <Button type="submit" size="icon" className="rounded-full shrink-0" data-testid="guild-post-send"><Send className="w-4 h-4" /></Button>
              </form>
            </div>
          </div>
          </>
        )}

        {g.is_member && (
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={leave} data-testid="guild-leave-btn"><DoorOpen className="w-4 h-4" /> {isOwner ? "Leave (transfers ownership)" : "Leave guild"}</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

const fmtLeft = (endIso) => {
  const ms = new Date(endIso).getTime() - Date.now();
  if (ms <= 0) return "ending…";
  const d = Math.floor(ms / 86400000), h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `${d}d ${h}h left`;
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h}h ${m}m left`;
};

function GuildBattle({ guildId, canManage, onChanged }) {
  const [data, setData] = useState(null);
  const [picking, setPicking] = useState(false);
  const [options, setOptions] = useState([]);
  const [opponent, setOpponent] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/guilds/${guildId}/challenges`).then((r) => setData(r.data)).catch(() => setData({}));
  }, [guildId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!data?.active) return;
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [data?.active, load]);

  const openPicker = async () => {
    setPicking(true);
    try {
      const { data: r } = await api.get("/guilds", { params: { sort: "popular" } });
      setOptions((r.guilds || []).filter((x) => x.id !== guildId));
    } catch { setOptions([]); }
  };
  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); setPicking(false); setOpponent(""); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const challenge = () => opponent && act(() => api.post(`/guilds/${guildId}/challenges`, { opponent_id: opponent }), "Challenge sent! ⚔️");
  const accept = (cid) => act(() => api.post(`/guilds/challenges/${cid}/accept`), "Battle started! 🔥");
  const decline = (cid) => act(() => api.post(`/guilds/challenges/${cid}/decline`), "Challenge declined.");
  const cancel = (cid) => act(() => api.post(`/guilds/challenges/${cid}/cancel`), "Challenge cancelled.");

  if (!data) return null;
  const { active, incoming = [], outgoing = [], history = [], stats = {} } = data;

  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 mb-3" data-testid="guild-battle">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold flex items-center gap-1.5"><Swords className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Guild Battle</span>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))]" data-testid="guild-battle-record">🏆 {stats.wins || 0}W · {stats.losses || 0}L</span>
      </div>

      {active ? (
        <ScoreBoard ch={active} />
      ) : (
        <>
          {incoming.map((c) => (
            <div key={c.id} className="flex items-center gap-2 text-sm bg-[hsl(var(--secondary))]/50 rounded-lg px-3 py-2 mb-2" data-testid={`battle-incoming-${c.id}`}>
              <span className="flex-1"><b>{c.challenger.emoji} {c.challenger.name}</b> challenged you to a 7-day XP race!</span>
              {canManage ? (
                <div className="flex gap-1.5">
                  <Button size="sm" className="rounded-full h-7" disabled={busy} onClick={() => accept(c.id)} data-testid={`battle-accept-${c.id}`}><Check className="w-3.5 h-3.5" /> Accept</Button>
                  <Button size="sm" variant="outline" className="rounded-full h-7" disabled={busy} onClick={() => decline(c.id)} data-testid={`battle-decline-${c.id}`}><X className="w-3.5 h-3.5" /></Button>
                </div>
              ) : <span className="text-[11px] text-[hsl(var(--muted-foreground))]">officers decide</span>}
            </div>
          ))}
          {outgoing.map((c) => (
            <div key={c.id} className="flex items-center gap-2 text-sm bg-[hsl(var(--secondary))]/50 rounded-lg px-3 py-2 mb-2" data-testid={`battle-outgoing-${c.id}`}>
              <Clock className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
              <span className="flex-1">Waiting for <b>{c.opponent.emoji} {c.opponent.name}</b> to accept…</span>
              {canManage && <Button size="sm" variant="ghost" className="rounded-full h-7 text-red-500" disabled={busy} onClick={() => cancel(c.id)}>Cancel</Button>}
            </div>
          ))}
          {!incoming.length && !outgoing.length && !picking && (
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-[hsl(var(--muted-foreground))]">Challenge another guild to a weekly XP race. Winner earns +200 XP!</span>
              {canManage && <Button size="sm" className="rounded-full shrink-0" onClick={openPicker} data-testid="battle-challenge-btn"><Swords className="w-3.5 h-3.5" /> Challenge</Button>}
            </div>
          )}
          {picking && (
            <div className="flex gap-2" data-testid="battle-picker">
              <Select value={opponent} onValueChange={setOpponent}>
                <SelectTrigger className="h-9" data-testid="battle-opponent-select"><SelectValue placeholder="Pick a rival guild…" /></SelectTrigger>
                <SelectContent>
                  {options.length === 0 ? <div className="px-3 py-2 text-xs text-[hsl(var(--muted-foreground))]">No other guilds yet</div>
                    : options.map((o) => <SelectItem key={o.id} value={o.id}>{o.emoji} {o.name} [{o.tag}] · Lv {o.level}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" className="rounded-full h-9" disabled={busy || !opponent} onClick={challenge} data-testid="battle-send">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send"}</Button>
              <Button size="sm" variant="ghost" className="rounded-full h-9" onClick={() => setPicking(false)}>Cancel</Button>
            </div>
          )}
          {history.length > 0 && (
            <div className="mt-2 pt-2 border-t border-[hsl(var(--border))] flex gap-1.5 overflow-x-auto" data-testid="battle-history">
              {history.filter((h) => h.status === "completed").slice(0, 5).map((h) => {
                const won = h.winner_id === (h.is_challenger ? h.challenger.id : h.opponent.id);
                const foe = h.is_challenger ? h.opponent : h.challenger;
                return <span key={h.id} className={`shrink-0 text-[11px] rounded-full px-2 py-0.5 ${won ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/10 text-red-400"}`}>{won ? "W" : "L"} vs {foe.emoji}{foe.tag}</span>;
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ScoreBoard({ ch }) {
  const cs = ch.score?.challenger || 0, os = ch.score?.opponent || 0;
  const total = cs + os, cPct = total ? (cs / total) * 100 : 50;
  return (
    <div data-testid="battle-scoreboard">
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="font-bold truncate">{ch.challenger.emoji} {ch.challenger.tag}</span>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtLeft(ch.end_at)}</span>
        <span className="font-bold truncate text-right">{ch.opponent.tag} {ch.opponent.emoji}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-display font-extrabold text-lg tabular-nums" data-testid="battle-score-challenger">{cs}</span>
        <div className="flex-1 h-2.5 rounded-full bg-red-500/25 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff] transition-all" style={{ width: `${cPct}%` }} />
        </div>
        <span className="font-display font-extrabold text-lg tabular-nums" data-testid="battle-score-opponent">{os}</span>
      </div>
      <div className="text-[10px] text-center text-[hsl(var(--muted-foreground))] mt-1.5">XP earned this battle · Winner gets +{ch.reward_xp || 200} XP</div>
    </div>
  );
}
